import { useState, useEffect, useCallback, useRef } from 'react';
import qz from 'qz-tray';

// QZ Tray connection and printing hook
export const useQzTray = () => {
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [printers, setPrinters] = useState([]);
  const connectionAttempted = useRef(false);

  // Connect to QZ Tray
  const connect = useCallback(async () => {
    if (connected || connecting) return;
    
    setConnecting(true);
    setError(null);
    
    try {
      // Check if already connected
      if (qz.websocket.isActive()) {
        setConnected(true);
        setConnecting(false);
        return;
      }

      // Configure certificate (use demo for testing)
      qz.security.setCertificatePromise((resolve) => {
        resolve(`-----BEGIN CERTIFICATE-----
MIIECzCCAvOgAwIBAgIJALHiHVcXNqp3MA0GCSqGSIb3DQEBCwUAMIGaMQswCQYD
VQQGEwJVUzELMAkGA1UECAwCTlkxEjAQBgNVBAcMCUNhbmFzdG90YTEbMBkGA1UE
CgwSUVogSW5kdXN0cmllcywgTExDMRswGQYDVQQLDBJRWiBJbmR1c3RyaWVzLCBM
TEMxEjAQBgNVBAMMCWxvY2FsaG9zdDEcMBoGCSqGSIb3DQEJARYNc3VwcG9ydEBx
ei5pbzAeFw0yMTAzMDcyMjI1MzdaFw0yNzAzMDYyMjI1MzdaMIGaMQswCQYDVQQG
EwJVUzELMAkGA1UECAwCTlkxEjAQBgNVBAcMCUNhbmFzdG90YTEbMBkGA1UECgwS
UVogSW5kdXN0cmllcywgTExDMRswGQYDVQQLDBJRWiBJbmR1c3RyaWVzLCBMTEMx
EjAQBgNVBAMMCWxvY2FsaG9zdDEcMBoGCSqGSIb3DQEJARYNc3VwcG9ydEBxei5p
bzCCASIwDQYJKoZIhvcNAQEBBQADggEPADCCAQoCggEBALFDKntsvBvdieO9wKOd
pYO9GxTQDITwmC0+F9Q7IZsA3H3RJVPJjMqCPn1Nl3jPGXHB8S4gHq1P5kG8yf+2
Ii3P10Kk+LPaqy5UtRCgNZXPJ8XEZiPK0G3znRHVqFJvNBkC8TGNLoKY8OgZy6OG
xSGKxDNkZJLQ+qNi5C0yW0TgKHm8F6TbYnqUqCQaTmB7R0qkTsBbFc/n7ETnGJaB
bGsWpkVT3GLRIBMq3hVCRJmQqKz2lMI3L7lZrVbqzQi3SZXB+Wl0UKb8gLHB8C7k
zHiKq0ToCqqm2Lkz5l1yFknqCmg5X/kfpKKqHPnLfDFRxZhsZCxpnKbvJvHC6kFZ
tVkCAwEAAaNTMFEwHQYDVR0OBBYEFI8FDKaXiYGQj8TI0zKHRNEaW7LDMB8GA1Ud
IwQYMBaAFI8FDKaXiYGQj8TI0zKHRNEaW7LDMA8GA1UdEwEB/wQFMAMBAf8wDQYJ
KoZIhvcNAQELBQADggEBAK1O4I3hcCDOeqepENrumADU/UJjgXYOXq4ICKC2cZwk
GNBc3fhQKe4GNn1gv9XQBQX0fvXk+kBPnWGZJMOQpfV0vRvHdL0wHsGNSYHe+PdR
bWG7c0x/8c8Q5RG7gJA0I7rHNP4ukX8HK63jAQE6RArOvn4RzJ7E7gGJvP0v4TKU
0LDvqeO8j3IM3h1P95M9x3E1G2LP0zdCQRHCpPNyZbnqkZXXPmkzQXLYJEM5Kvcd
0TqFKCqzGl5fGjKxQh0nHFI0/bJu1B4PH7g2RzgFpvQBF/3cvP6x0T/xh9Bf0nXk
2Z7IFvDPCOi9xcq1Lw0fFnC8TxN/bGPJsqm0h3bSzPQ=
-----END CERTIFICATE-----`);
      });

      // Use demo signature for testing (in production, use your own signed certificate)
      qz.security.setSignatureAlgorithm('SHA512');
      qz.security.setSignaturePromise((toSign) => {
        return (resolve) => {
          // Demo signature - works for localhost/testing
          resolve(); // Skip signature for demo
        };
      });

      await qz.websocket.connect();
      setConnected(true);
      
      // Get available printers
      const availablePrinters = await qz.printers.find();
      setPrinters(availablePrinters);
      
    } catch (err) {
      console.error('QZ Tray connection error:', err);
      setError(err.message || 'Failed to connect to QZ Tray');
      setConnected(false);
    } finally {
      setConnecting(false);
    }
  }, [connected, connecting]);

  // Disconnect from QZ Tray
  const disconnect = useCallback(async () => {
    if (!connected) return;
    
    try {
      await qz.websocket.disconnect();
      setConnected(false);
    } catch (err) {
      console.error('QZ Tray disconnect error:', err);
    }
  }, [connected]);

  // Get list of available printers
  const getPrinters = useCallback(async () => {
    if (!connected) {
      await connect();
    }
    
    try {
      const availablePrinters = await qz.printers.find();
      setPrinters(availablePrinters);
      return availablePrinters;
    } catch (err) {
      console.error('Error getting printers:', err);
      setError(err.message);
      return [];
    }
  }, [connected, connect]);

  // Print HTML content to a specific printer
  const printHtml = useCallback(async (printerName, htmlContent, options = {}) => {
    if (!printerName) {
      throw new Error('Printer name is required');
    }

    try {
      if (!connected) {
        await connect();
      }

      const config = qz.configs.create(printerName, {
        colorType: 'grayscale',
        copies: options.copies || 1,
        ...options.config
      });

      const data = [{
        type: 'html',
        format: 'plain',
        data: htmlContent
      }];

      await qz.print(config, data);
      return { success: true };
    } catch (err) {
      console.error('Print error:', err);
      throw new Error(err.message || 'Failed to print');
    }
  }, [connected, connect]);

  // Print raw data (ESC/POS commands for receipt printers)
  const printRaw = useCallback(async (printerName, rawCommands, options = {}) => {
    if (!printerName) {
      throw new Error('Printer name is required');
    }

    try {
      if (!connected) {
        await connect();
      }

      const config = qz.configs.create(printerName, {
        ...options.config
      });

      const data = [{
        type: 'raw',
        format: 'base64',
        data: btoa(rawCommands)
      }];

      await qz.print(config, data);
      return { success: true };
    } catch (err) {
      console.error('Raw print error:', err);
      throw new Error(err.message || 'Failed to print');
    }
  }, [connected, connect]);

  // Print PDF file
  const printPdf = useCallback(async (printerName, pdfData, options = {}) => {
    if (!printerName) {
      throw new Error('Printer name is required');
    }

    try {
      if (!connected) {
        await connect();
      }

      const config = qz.configs.create(printerName, {
        ...options.config
      });

      const data = [{
        type: 'pdf',
        format: 'base64',
        data: pdfData
      }];

      await qz.print(config, data);
      return { success: true };
    } catch (err) {
      console.error('PDF print error:', err);
      throw new Error(err.message || 'Failed to print');
    }
  }, [connected, connect]);

  // Test printer connection
  const testPrinter = useCallback(async (printerName) => {
    try {
      if (!connected) {
        await connect();
      }

      // Check if printer exists
      const availablePrinters = await qz.printers.find();
      const printerExists = availablePrinters.some(
        p => p.toLowerCase() === printerName.toLowerCase() || 
             p.includes(printerName) ||
             printerName.includes(p)
      );

      if (!printerExists) {
        return { 
          success: false, 
          message: `Printer "${printerName}" not found. Available: ${availablePrinters.slice(0, 5).join(', ')}...`
        };
      }

      // Try to print a test page
      const config = qz.configs.create(printerName);
      const testHtml = `
        <div style="font-family: Arial; padding: 20px; text-align: center;">
          <h2>QZ Tray Test Print</h2>
          <p>Printer: ${printerName}</p>
          <p>Date: ${new Date().toLocaleString()}</p>
          <p>✓ Connection successful!</p>
        </div>
      `;

      await qz.print(config, [{
        type: 'html',
        format: 'plain',
        data: testHtml
      }]);

      return { success: true, message: 'Test page sent to printer!' };
    } catch (err) {
      return { success: false, message: err.message };
    }
  }, [connected, connect]);

  // Auto-connect on mount (only once)
  useEffect(() => {
    if (!connectionAttempted.current) {
      connectionAttempted.current = true;
      // Delay connection attempt to avoid blocking render
      const timer = setTimeout(() => {
        connect().catch(() => {
          // Silently fail - QZ Tray might not be installed
        });
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [connect]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (qz.websocket.isActive()) {
        qz.websocket.disconnect().catch(() => {});
      }
    };
  }, []);

  return {
    connected,
    connecting,
    error,
    printers,
    connect,
    disconnect,
    getPrinters,
    printHtml,
    printRaw,
    printPdf,
    testPrinter
  };
};

export default useQzTray;
